import React, { useCallback, useMemo, useContext, useState } from 'react';
import {
    TextField,
    Dropdown,
    DropdownItem,
    makeStyles
} from '@ellucian/react-design-system/core';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import PropTypes from 'prop-types';
import flags from 'react-phone-number-input/flags';
import { sizingXxLarge, spacing30 } from '@ellucian/react-design-system/core/styles/tokens';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import get from 'lodash/get';
import head from 'lodash/head';
import identity from 'lodash/identity';
import toPath from 'lodash/toPath';

const PhoneContext = React.createContext({ countryChanged: false, setCountryChanged: () => { } });
const useStyles = makeStyles((theme) => ({
    [theme.breakpoints.up('md')]: {
        phoneInput: {
            display: 'flex !important',
            alignItems: 'start !important',
        },
    },
    dropdownIcon: {
        right: `${spacing30} !important`,
    },
    selectMenu: {
        minHeight: ['1.25rem', '!important'],
        minWidth: sizingXxLarge,
    },
    [theme.breakpoints.down('sm')]: {
        phoneInput: {
            display: 'block !important',
        }
    },
}));

function getSelectedOption(options, value) {
    for (const option of options) {
        if (option.value === value) {
            return option;
        }
    }
}

const DropdownWrapper = (props) => {
    const classes = useStyles();
    const {
        value,
        onChange,
        options,
        iconComponent: Icon,
        ...rest
    } = props;
    const onChangeCallback = useCallback((event) => {
        const value = event.target.value;
        onChange(value);
    }, [onChange]);

    const selectedOption = useMemo(() => {
        return getSelectedOption(options, value);
    }, [options, value]);

    return (
        <div>
            <Dropdown
                classes={{ icon: classes.dropdownIcon, select: classes.selectMenu }}
                {...rest}
                value={value}
                onChange={onChangeCallback}
                renderValue={() => <Icon country={selectedOption && selectedOption.value} label={selectedOption && selectedOption.label} />}
            >
                {options.map((country) => (
                    country.value &&
                    <DropdownItem
                        key={country.label}
                        value={country.value || country.label}
                        LeftIconComponent={
                            <Icon
                                country={country.value}
                                label={country.label && country.label}
                            />
                        }
                    >
                        {country.label}
                    </DropdownItem>
                ))}
            </Dropdown>
        </div>
    );
};

DropdownWrapper.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
        divider: PropTypes.bool
    })).isRequired,
    iconComponent: PropTypes.elementType,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    tabIndex: PropTypes.any,
};

const TextFieldWrapper = React.forwardRef((props, ref) => {
    const {
        helperText: helperTextProp,
        onFocus: onFocusProp,
        required,
        ...rest
    } = props;
    const { name } = useContext(PhoneContext);
    
    const selectAlwaysShowError = useMemo(() => {
        return createSelector(
            state => get(state.form.meta, `forceShowErrors.${head(toPath(name))}`),
            value => !!value
        );
    }, [name]);
    const selectError = useMemo(() => {
        return createSelector(
            state => get(state.form.errors, toPath(name)),
            identity
        );
    }, [name]);
    const selectTouched = useMemo(() => {
        return createSelector(
            state => get(state.form.touched, toPath(name)),
            identity
        );
    }, [name]);
    const onFocus = useCallback((...args) => {
        if (ref && ref.current) {
            ref.current.selectionStart = ref.current.value.length;
            ref.current.selectionEnd = ref.current.value.length;
        }
        onFocusProp(...args);
    }, [ref, ref?.current, onFocusProp]);

    const error = useSelector(selectError);
    const touched = useSelector(selectTouched);
    const alwaysShowError = useSelector(selectAlwaysShowError);
    let hasError = false;
    let helperText = helperTextProp;
    if (error && (touched || alwaysShowError)) {
        hasError = true;
        helperText = error;
    }
    return (
        <TextField
            {...rest}
            error={hasError}
            fullWidth
            helperText={helperText}
            inputProps={{ ref }}
            name={name}
            onFocus={onFocus}
            required={required}
        />
    );
});
TextFieldWrapper.displayName = 'TextFieldWrapper';

TextFieldWrapper.propTypes = {
    helperText: PropTypes.string,
    onFocus: PropTypes.func,
    required: PropTypes.bool,
};

const PhoneNumber = props => {
    const classes = useStyles();
    const { value, onBlur, name: nameProp, onFocus, onChange, required, countryLabel = '', ...rest } = props;
    const [countryChanged, setCountryChanged] = useState(false);
    const ctx = useMemo(() => ({
        countryChanged,
        name: nameProp,
        setCountryChanged,
    }), [nameProp, countryChanged]);
    return (
        <PhoneContext.Provider value={ctx}>
            <PhoneInput
                flags={flags}
                value={value}
                onChange={onChange}
                inputComponent={TextFieldWrapper}
                required={required}
                {...rest}
                onBlur={onBlur}
                onFocus={onFocus}
                international
                countrySelectComponent={DropdownWrapper}
                countrySelectProps={{ label: countryLabel }}
                className={classes.phoneInput}
                onCountryChange={() => setCountryChanged(true)}
            />
        </PhoneContext.Provider>
    );
};

PhoneNumber.propTypes = {
    countryLabel: PropTypes.string,
    name: PropTypes.string.isRequired,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    required: PropTypes.bool,
    value: PropTypes.string,
};

export default PhoneNumber;
